<template>
  <div>
    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="filteredItems"
      :search="search"
    >
      <template v-slot:item.signature="{ item }">
        <originator-signature
          :item="item"
          @addSignature="$emit('addSignature', item)"
        />
      </template>
      <template v-slot:item.roles="{ item }">
        <simple-list :items="item.specialized_roles" />
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="my-3">
          <v-btn
            small
            :id="`users-table-edit-btn-${item.id}`"
            @click="() => $emit('edit', item)"
            v-if-has-permission:hide="editUserPermission"
          >
            Edit User
          </v-btn>
        </div>
        <div class="mt-3">
          <v-btn
            small
            color="secondary"
            :id="`users-table-history-btn-${item.id}`"
            @click="() => $emit('history', item)"
            v-if-has-permission:hide="viewHistoryPermission"
          >
            View History
          </v-btn>
        </div>
        <div class="mt-3">
          <v-btn
            small
            color="warning"
            :id="`users-table-sync-salesforce-btn-${item.id}`"
            @click="syncSalesforce(item)"
            v-if="item.salesforce_out_of_sync"
        >
            Sync to Salesforce
          </v-btn>
        </div>
        <div class="my-3" v-if="item._id !== user._id && item.active === true">
          <v-btn
            text
            small
            color="primary"
            :id="`users-table-change-pwd-btn-${item.id}`"
            :disabled="item.loading"
            @click="openConfirmDialog(item)"
            v-if-has-permission:hide="editUserPermission"
          >
            Invalidate Password
          </v-btn>
        </div>
        <div v-else class="mb-4"></div>
      </template>
    </v-data-table>
    <anchor-confirm-dialog
      ref="confirmDialog"
      :title="confirmDialogTitle"
      :max-width="600"
      @cancel="userToInvalidate = null"
      @confirm="invalidateUserPassword"
    />
  </div>
</template>

<script>
import SimpleList from '@/components/utils/SimpleList';
import OriginatorSignature from '@/components/admin/controls/users/tables/OriginatorSignature';
import Vue from 'vue';

export default {
  name: 'AdminUsersTable',
  components: {
    SimpleList,
    OriginatorSignature,
  },
  props: {
    search: String,
    activeOnly: Boolean,
  },
  data: () => ({
    items: [],
    headers: [
      { text: 'Name', value: 'name' },
      { text: 'Username', value: 'username' },
      { text: 'Display Name', value: 'display_name' },
      { text: 'Email', value: 'email' },
      { text: 'Roles', value: 'roles' },
      { text: 'Signature', value: 'signature', valign: 'top' },
      { text: 'Active', value: 'active' },
      { text: 'Show on App', value: 'show_on_app' },
      { text: 'Actions', value: 'actions', width: 300 },
    ],
    loading: false,
    userToInvalidate: null,
  }),
  computed: {
    editUserPermission: vm => vm.$permissions.DELETE_DOMAIN,
    viewHistoryPermission: vm => vm.$permissions.VIEW_USER_HISTORY,
    user() {
      return this.$store.getters.user;
    },
    confirmDialogTitle() {
      if (!this.userToInvalidate) { return ''; }
      return `Are you sure you want to invalidate the password for ${this.userToInvalidate.name}?`;
    },
    filteredItems() {
      if (this.activeOnly) {
        return this.items.filter(item => item.active);
      }
      return this.items;
    },
  },
  methods: {
    openConfirmDialog(user) {
      this.userToInvalidate = user;
      this.$refs.confirmDialog.open();
    },
    invalidateUserPassword() {
      /* eslint no-underscore-dangle: ["error", { "allow": ["_id"] }] */
      if (!this.userToInvalidate) { return; }

      this.$set(this.userToInvalidate, 'loading', true);
      this.$requests.post('/auth/reset-user-password/', { user_id: this.userToInvalidate._id })
        .then(response => {
          this.$toast.success(response.data.message, { timeout: 3000 });
        })
        .catch(err => {
          this.$toast.error(err.response.data.message, { timeout: 3000 });
        })
        .then(() => {
          this.$delete(this.userToInvalidate, 'loading');
          this.userToInvalidate = null;
        });
    },
    syncSalesforce(item) {
      if (!item || !item._id) {
        this.$toast.error('There was an error when syncing this user.', { timeout: 3000 });
        return;
      }

      const url = `/v1/users/${item._id}/sync_salesforce`;

      this.$requests.post(url)
        .then(response => {
          const stillOutOfSync = response.data.user.salesforce_out_of_sync;
          if (stillOutOfSync) {
            this.$toast.error('There was an error when syncing the user!', { timeout: 3000 });
          } else {
            Vue.set(item, 'salesforce_out_of_sync', stillOutOfSync);
            this.$toast.success('User synced successfully!', { timeout: 3000 });
          }
        })
        .catch(() => {
          this.$toast.error('There was an error when syncing the user!', { timeout: 3000 });
        });
    },
    fetch() {
      this.loading = true;
      this.$requests.get('/v1/users')
        .then(response => {
          this.items = response.data.map(item => {
            const tmp = item;
            tmp.name = `${item.first_name} ${item.last_name}`.trim() || '-';
            return tmp;
          });
        })
        .catch(() => {
          this.$toast.error('Could not resolve users...', { timeout: 3000 });
        })
        .then(() => {
          this.loading = false;
        });
    },
  },
  mounted() {
    this.fetch();
  },
};
</script>
